import React, { useState, useEffect } from 'react';
import { config } from '../config';
import Spinner from '../components/Spinner';
import { useNavigate, useParams } from 'react-router-dom';
import { api, getAuthHeaders } from '../services/api';
import { useNotification } from '../components/Notification';
import { getCurrencySymbol } from '../utils/currency';
import './Invoices.css';
import './DetailPages.css';

function InvoiceDetail({ tenantId }) {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { id } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [repair, setRepair] = useState(null);
  const [car, setCar] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [hourlyRates, setHourlyRates] = useState([]);
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewInvoiceId, setPreviewInvoiceId] = useState(null);
  const [previewPdfUrl, setPreviewPdfUrl] = useState(null);

  useEffect(() => {
    if (tenantId && id) {
      loadData();
    }
  }, [tenantId, id]);

  const loadData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadTenant(),
        loadInvoice()
      ]);
    } finally {
      setLoading(false);
    }
  };

  const loadTenant = async () => {
    try {
      const data = await api.get('/tenants', null);
      const currentTenant = data.find(t => t.id === tenantId) || data[0];
      setTenant(currentTenant);
    } catch (error) {
      console.error('Error loading tenant:', error);
    }
  };

  const currency = getCurrencySymbol(tenant?.taxSystem || 'DE');

  const loadInvoice = async () => {
    try {
      const data = await api.get(`/invoices/${id}`, tenantId);
      setInvoice(data);
      
      if (data.repairId) {
        loadRepair(data.repairId);
      }
      if (data.carId) {
        loadCar(data.carId);
      }
      if (data.customerId) {
        loadCustomer(data.customerId);
      }
      await loadHourlyRates();
    } catch (error) {
      console.error('Error loading invoice:', error);
      showNotification('Fehler beim Laden der Rechnung', 'error');
      navigate('/invoices');
    }
  };

  const loadRepair = async (repairId) => {
    try {
      const data = await api.get(`/repairs/${repairId}`, tenantId);
      setRepair(data);
    } catch (error) {
      console.error('Error loading repair:', error);
    }
  };

  const loadCar = async (carId) => {
    try {
      const data = await api.get(`/cars/${carId}`, tenantId);
      setCar(data);
    } catch (error) {
      console.error('Error loading car:', error);
    }
  };

  const loadCustomer = async (customerId) => {
    try {
      const data = await api.get(`/customers/${customerId}`, tenantId);
      setCustomer(data);
    } catch (error) {
      console.error('Error loading customer:', error);
    }
  };

  const loadHourlyRates = async () => {
    try {
      const data = await api.get('/hourly-rates', tenantId);
      setHourlyRates(data);
    } catch (error) {
      console.error('Error loading hourly rates:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: currency === 'CHF' ? 'CHF' : 'EUR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const getStatusLabel = (status) => {
    const labels = {
      draft: 'Entwurf',
      sent: 'Versendet',
      paid: 'Bezahlt',
      overdue: 'Überfällig'
    };
    return labels[status] || status;
  };

  const getHourlyRate = (hourlyRateId) => {
    const rate = hourlyRates.find(r => r.id === hourlyRateId);
    return rate ? rate.rate : 0;
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await fetch(`${config.API_URL || 'https://api.garagedesk.net/api'}/invoices/${id}/pdf`, {
        headers: getAuthHeaders(tenantId)
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Rechnung-${invoice.invoiceNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      showNotification('Fehler beim Herunterladen der PDF: ' + (error.message || 'Unbekannter Fehler', 'error'));
    }
  };

  const handlePreviewPDF = async () => {
    try {
      setPreviewInvoiceId(id);
      const response = await fetch(`${config.API_URL || 'https://api.garagedesk.net/api'}/invoices/${id}/preview`, {
        headers: getAuthHeaders(tenantId)
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      setPreviewPdfUrl(url);
    } catch (error) {
      console.error('Error loading PDF preview:', error);
      showNotification('Fehler beim Laden der PDF-Vorschau: ' + (error.message || 'Unbekannter Fehler', 'error'));
      setPreviewInvoiceId(null);
    }
  };

  const handleEmail = async () => {
    if (!customer?.email) {
      showNotification('Keine E-Mail-Adresse für diesen Kunden vorhanden', 'error');
      return;
    }

    try {
      await handleDownloadPDF();
      
      setTimeout(() => {
        const customerName = customer ? `${customer.firstName} ${customer.lastName}` : '';
        const tenantName = tenant?.name || 'GarageDesk';
        
        const subject = encodeURIComponent(`Rechnung ${invoice.invoiceNumber} - ${tenantName}`);
        const body = encodeURIComponent(
          `Sehr geehrte/r ${customerName},\n\n` +
          `anbei erhalten Sie die Rechnung ${invoice.invoiceNumber} vom ${formatDate(invoice.date)}.\n\n` +
          `Rechnungsbetrag: ${formatCurrency(invoice.grossAmount || 0)}\n` +
          (invoice.dueDate ? `Fälligkeitsdatum: ${formatDate(invoice.dueDate)}\n` : '') +
          `\nBitte überweisen Sie den Betrag auf unser Konto.\n\n` +
          `Mit freundlichen Grüßen\n${tenantName}`
        );
        
        const mailtoLink = `mailto:${customer.email}?subject=${subject}&body=${body}`;
        window.location.href = mailtoLink;
      }, 500);
    } catch (error) {
      console.error('Error preparing email:', error);
      showNotification('Fehler beim Vorbereiten der E-Mail: ' + (error.message || 'Unbekannter Fehler', 'error'));
    }
  };

  if (loading) {
    return <Spinner />;
  }

  if (!invoice) {
    return <div>Rechnung nicht gefunden</div>;
  }

  const materials = repair?.materials || [];
  const labor = repair?.labor || [];
  const totalMaterialsAmount = materials.reduce((sum, item) => sum + (item.amount || 0) * (item.quantity || 1), 0);
  let totalLaborAmount = 0;
  for (const lab of labor) {
    if (lab.hours && lab.hourlyRateId) {
      totalLaborAmount += (lab.hours || 0) * getHourlyRate(lab.hourlyRateId);
    }
  }

  return (
    <div className="invoice-detail">
      <div className="detail-header">
        <button type="button" onClick={() => navigate('/invoices')} className="btn-secondary">
          ← Zurück
        </button>
        <h1>Rechnung: {invoice.invoiceNumber}</h1>
        <div className="detail-actions">
          <button type="button" onClick={handlePreviewPDF} className="btn-secondary">
            Vorschau
          </button>
          <button type="button" onClick={handleDownloadPDF} className="btn-secondary">
            PDF herunterladen
          </button>
          {customer?.email && (
            <button type="button" onClick={handleEmail} className="btn-secondary">
              E-Mail
            </button>
          )}
        </div>
      </div>

      <div className="detail-section">
        <h2>Allgemeine Informationen</h2>
        <p><strong>Status:</strong> {getStatusLabel(invoice.status)}</p>
        <p><strong>Datum:</strong> {formatDate(invoice.date)}</p>
        {invoice.dueDate && <p><strong>Fälligkeitsdatum:</strong> {formatDate(invoice.dueDate)}</p>}
        <p><strong>Nettobetrag:</strong> {formatCurrency(invoice.netAmount || 0)}</p>
        <p><strong>MwSt.:</strong> {formatCurrency(invoice.taxAmount || ((invoice.grossAmount || 0) - (invoice.netAmount || 0)))}</p>
        <p><strong>Gesamtbetrag:</strong> {formatCurrency(invoice.grossAmount || 0)}</p>
      </div>

      {customer && (
        <div className="detail-section">
          <h2>Kunde</h2>
          <p>
            <span 
              className="link-button"
              onClick={() => navigate(`/customers/${customer.id}`)}
            >
              {customer.firstName} {customer.lastName}
            </span>
          </p>
          {customer.email && <p><strong>E-Mail:</strong> {customer.email}</p>}
          {customer.phone && <p><strong>Telefon:</strong> {customer.phone}</p>}
        </div>
      )}

      {car && (
        <div className="detail-section">
          <h2>Fahrzeug</h2>
          <p>
            <span 
              className="link-button"
              onClick={() => navigate(`/cars/${car.id}`)}
            >
              {car.make} {car.model} {car.year} ({car.licensePlate || 'Kein Kennzeichen'})
            </span>
          </p>
        </div>
      )}

      {repair && (
        <div className="detail-section">
          <h2>Reparatur</h2>
          <p>
            <span 
              className="link-button"
              onClick={() => navigate(`/repairs/${repair.id}`)}
            >
              {repair.description || 'Keine Beschreibung'}
            </span>
          </p>
        </div>
      )}

      {materials.length > 0 && (
        <div className="detail-section">
          <h2>Materialien</h2>
          <table className="data-table">
            <thead>
              <tr>
                <th>Beschreibung</th>
                <th>Menge</th>
                <th>Einzelpreis</th>
                <th>Gesamt</th>
              </tr>
            </thead>
            <tbody>
              {materials.map((item, idx) => (
                <tr key={idx}>
                  <td>{item.description || '-'}</td>
                  <td>{item.quantity || 1}</td>
                  <td>{formatCurrency(item.amount || 0)}</td>
                  <td>{formatCurrency((item.amount || 0) * (item.quantity || 1))}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan="3"><strong>Zwischensumme Materialien:</strong></td>
                <td><strong>{formatCurrency(totalMaterialsAmount)}</strong></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {labor.length > 0 && (
        <div className="detail-section">
          <h2>Arbeitseinsatz</h2>
          <table className="data-table">
            <thead>
              <tr>
                <th>Beschreibung</th>
                <th>Stunden</th>
                <th>Stundensatz</th>
                <th>Gesamt</th>
              </tr>
            </thead>
            <tbody>
              {labor.map((lab, idx) => {
                const rate = getHourlyRate(lab.hourlyRateId);
                const amount = (lab.hours || 0) * rate;
                return (
                  <tr key={idx}>
                    <td>
                      {lab.description || '-'}
                      {(lab.date || lab.mileage || lab.documentation) && (
                        <div style={{ fontSize: '0.85em', color: '#666', marginTop: '4px' }}>
                          {lab.date && <span>Datum: {formatDate(lab.date)}</span>}
                          {lab.mileage && <span> | Kilometerstand: {lab.mileage.toLocaleString('de-DE')} km</span>}
                          {lab.documentation && (
                            <div style={{ marginTop: '4px' }}>{lab.documentation}</div>
                          )}
                        </div>
                      )}
                    </td>
                    <td>{lab.hours || 0}</td>
                    <td>{formatCurrency(rate)}/h</td>
                    <td>{formatCurrency(amount)}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan="3"><strong>Zwischensumme Arbeitseinsatz:</strong></td>
                <td><strong>{formatCurrency(totalLaborAmount)}</strong></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {previewInvoiceId && previewPdfUrl && (
        <div className="modal-overlay" onClick={() => {
          setPreviewInvoiceId(null);
          if (previewPdfUrl) {
            window.URL.revokeObjectURL(previewPdfUrl);
            setPreviewPdfUrl(null);
          }
        }}>
          <div className="modal-content pdf-preview" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>PDF-Vorschau</h2>
              <button type="button" className="modal-close" onClick={() => {
                setPreviewInvoiceId(null);
                if (previewPdfUrl) {
                  window.URL.revokeObjectURL(previewPdfUrl);
                  setPreviewPdfUrl(null);
                }
              }}>×</button>
            </div>
            <div className="modal-body">
              <iframe
                src={previewPdfUrl}
                style={{ width: '100%', height: '600px', border: 'none' }}
                title="PDF Preview"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default InvoiceDetail;

